import { createEffect, onCleanup } from 'solid-js'

import { useI18n } from '../../i18n'
import {
   closeShareOverlay,
   getShareQrImageUrl,
   shareOverlayState,
} from '../../state/shareOverlayStore'

/**
 * Renders the global share/QR overlay.
 *
 * @returns The overlay element.
 */
const GlobalShareOverlay = () => {
   const t = useI18n()

   createEffect(() => {
      if (!shareOverlayState().isOpen || typeof document === 'undefined') {
         return
      }

      /**
       * Closes the overlay on Escape.
       *
       * @param event - The keydown event.
       */
      const handleKeyDown = (event: KeyboardEvent) => {
         if (event.key === 'Escape') {
            closeShareOverlay()
         }
      }

      document.addEventListener('keydown', handleKeyDown)
      onCleanup(() => document.removeEventListener('keydown', handleKeyDown))
   })

   return (
      <div
         id="global-share-overlay"
         class="fixed inset-0 z-50 flex items-center justify-center bg-surface/90 p-0 sm:p-4"
         classList={{ hidden: !shareOverlayState().isOpen }}
         role="dialog"
         aria-modal="true"
         onClick={event => {
            if (event.currentTarget === event.target) {
               closeShareOverlay()
            }
         }}
      >
         <div class="flex h-full w-full flex-col justify-center space-y-4 overflow-y-auto rounded-none bg-surface-raised p-6 text-center text-ink shadow-2xl shadow-surface-sunken sm:h-auto sm:max-w-md sm:rounded-2xl sm:p-8">
            <h2 id="global-share-title" class="text-lg font-semibold text-ink">
               {shareOverlayState().title || t('Share Faster · Leaner · Stronger')}
            </h2>
            <p id="global-share-description" class="text-sm text-ink-secondary">
               {shareOverlayState().description ||
                  t('Scan this QR code or open the link below on another device.')}
            </p>
            <img
               id="global-share-qr-image"
               src={
                  shareOverlayState().url ?
                     getShareQrImageUrl(shareOverlayState().url)
                  :  ''
               }
               alt={t('Faster · Leaner · Stronger share QR code')}
               class="mx-auto h-full max-h-[18rem] w-full max-w-[18rem] rounded-lg border border-edge-soft bg-white p-3"
            />
            <a
               id="global-share-link"
               href={shareOverlayState().url || 'https://fasting-timer.pages.dev/'}
               target="_blank"
               rel="noopener"
               class="block break-all text-xs text-emerald-300 hover:text-emerald-200"
            >
               {shareOverlayState().url || 'https://fasting-timer.pages.dev/'}
            </a>
            <button
               id="global-share-close-btn"
               type="button"
               onClick={closeShareOverlay}
               class="w-full rounded-lg border border-edge bg-surface-control px-4 py-2 text-sm font-semibold text-ink-soft transition hover:bg-surface-control-hover focus:outline-none focus:ring-2 focus:ring-ink-muted focus:ring-offset-2 focus:ring-offset-surface"
            >
               {t('Close')}
            </button>
         </div>
      </div>
   )
}

export default GlobalShareOverlay
